/**
 * overlaysApi.ts — Viewport-bounded fetcher for generic POINT map overlays
 * (public toilets first; future: dump points, rest areas…).
 *
 * Backend: GET /api/overlays/<type>?bbox=west,south,east,north
 *   -> { type:"FeatureCollection", features:[Point…], status, attribution, truncated }
 *
 * 1-deep per-(type,bbox) cache + graceful degrade, mirroring publicLandsApi.ts.
 */

const API_BASE =
  (process.env.NEXT_PUBLIC_API_URL || 'https://app.trip-planner.com.au') + '/api';

export interface OverlayPointProps {
  id: string;
  name: string;
  type?: string;
  state?: string;
  addr?: string;
  hours?: string;
  url?: string;
  /** brand/operator, present on fuel-station points */
  brand?: string;
  /** list of TRUE feature flags, e.g. ["accessible","dumpPoint","water"] */
  f?: string[];
}

export interface OverlayPointFeature {
  type: 'Feature';
  id?: string | number;
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: OverlayPointProps;
}

export interface OverlayPointsResponse {
  type: 'FeatureCollection';
  features: OverlayPointFeature[];
  status: 'ok' | 'pipeline_not_run' | 'zoom_in' | 'error';
  truncated?: boolean;
  total_in_view?: number;
  attribution?: string | null;
  message?: string | null;
}

export type Bbox = { west: number; south: number; east: number; north: number };

const cache = new Map<string, OverlayPointsResponse>();

function key(type: string, b: Bbox): string {
  // 4dp ≈ 11m at the equator — fine-grained enough that street-level pans
  // don't collide on the same key (3dp ≈ 110m was too coarse at zoom ≥16).
  return `${type}|${b.west.toFixed(4)},${b.south.toFixed(4)},${b.east.toFixed(4)},${b.north.toFixed(4)}`;
}

export async function fetchOverlayPoints(
  type: string,
  bbox: Bbox,
  signal?: AbortSignal
): Promise<OverlayPointsResponse> {
  const k = key(type, bbox);
  const hit = cache.get(k);
  if (hit) return hit;

  const url = new URL(`${API_BASE}/overlays/${type}`);
  url.searchParams.set('bbox', `${bbox.west},${bbox.south},${bbox.east},${bbox.north}`);

  try {
    const res = await fetch(url.toString(), { signal });
    if (!res.ok) {
      return { type: 'FeatureCollection', features: [], status: 'pipeline_not_run', message: `Endpoint returned ${res.status}` };
    }
    const data = (await res.json()) as OverlayPointsResponse;
    const out: OverlayPointsResponse = {
      type: 'FeatureCollection',
      features: Array.isArray(data.features) ? data.features : [],
      status: data.status ?? 'ok',
      truncated: data.truncated,
      total_in_view: data.total_in_view,
      attribution: data.attribution,
      message: data.message,
    };
    if (out.status === 'ok') {
      cache.set(k, out);
      if (cache.size > 40) {
        // `Map.keys().next()` returns {done:true,value:undefined} on an empty
        // map. The previous `as string` cast lied — `Map.delete(undefined)`
        // silently no-ops and the cache grew unbounded.
        const first = cache.keys().next();
        if (!first.done) cache.delete(first.value);
      }
    }
    return out;
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') throw err;
    return { type: 'FeatureCollection', features: [], status: 'error', message: 'Network error' };
  }
}

/** A point from the /nearest endpoint — full record plus a computed distance. */
export interface OverlayNearestPoint extends OverlayPointProps {
  lat: number;
  lng: number;
  distance_km: number;
}

export interface OverlayNearestResponse {
  points: OverlayNearestPoint[];
  total: number;
  status: string;
  attribution?: string | null;
}

/**
 * Nearest points of a given overlay type to (lat,lng), server-computed.
 * Backend: GET /api/overlays/<type>/nearest?lat=&lng=&limit=&radius_km=
 * Used by the campsite fuel widget so we no longer pay Google Places per view.
 */
export async function fetchOverlayNearest(
  type: string,
  lat: number,
  lng: number,
  opts: { limit?: number; radiusKm?: number; signal?: AbortSignal } = {}
): Promise<OverlayNearestResponse> {
  const url = new URL(`${API_BASE}/overlays/${type}/nearest`);
  url.searchParams.set('lat', String(lat));
  url.searchParams.set('lng', String(lng));
  if (opts.limit) url.searchParams.set('limit', String(opts.limit));
  if (opts.radiusKm) url.searchParams.set('radius_km', String(opts.radiusKm));

  try {
    const res = await fetch(url.toString(), { signal: opts.signal });
    if (!res.ok) return { points: [], total: 0, status: 'error' };
    const data = (await res.json()) as OverlayNearestResponse;
    return {
      points: Array.isArray(data.points) ? data.points : [],
      total: data.total ?? 0,
      status: data.status ?? 'ok',
      attribution: data.attribution,
    };
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') throw err;
    return { points: [], total: 0, status: 'error' };
  }
}

/** Human labels for toilet feature flags (used in the marker popup). */
export const TOILET_FLAG_LABELS: Record<string, string> = {
  accessible: 'Wheelchair accessible',
  ambulant: 'Ambulant',
  mlak: 'MLAK / key required',
  babyChange: 'Baby change',
  changingPlaces: 'Adult change table',
  dumpPoint: 'RV dump point',
  water: 'Drinking water',
  shower: 'Shower',
  payment: 'Payment required',
  sanitary: 'Sanitary disposal',
  sharps: 'Sharps disposal',
  unisex: 'Unisex',
  allGender: 'All gender',
};
